// The run's conversation card (design/isomorphic run-conversation): the chat decrypted in
// this browser with the pending question cards in place, and a switch that shows exactly
// what the host stores instead. Pure: the live container (RunConversation) and the design
// preview both feed it a conversation state.
import { ChatsCircle, LockKey } from "@phosphor-icons/react";
import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { Switch } from "rizzui";

import type { Question } from "../../api/client";
import { EventList } from "../../conversation/EventList";
import { ShowWorkingSwitch } from "../../conversation/ShowWorkingSwitch";
import { HostRecord } from "../../conversation/HostRecord";
import type { ConversationState } from "../../conversation/model";
import { findQuestion, questionIdOf } from "../../conversation/question";
import { useT } from "../../i18n";
import { QuestionCard, type QuestionAccess } from "../../pages/QuestionCard";
import { WidgetCard } from "../../ui/Card";
import { EmptyState } from "../../ui/states";
import { E2EEBadge } from "../../ui/status";

export type ConversationSource =
  | { kind: "unlinked" }
  | { kind: "none" }
  | { kind: "live"; state: ConversationState; access: QuestionAccess & { kind: "linked" } };

interface Props {
  source: ConversationSource;
  /** Pending questions only (the API lists no others). */
  questions: Question[];
  agent: string;
  where?: string;
}

function readers(state: ConversationState): number | undefined {
  return Array.from(state.segments.values()).at(-1)?.open.keys.length;
}

export function ConversationCard({ source, questions, agent, where }: Props) {
  const { conversation } = useT();
  const t = conversation.card;
  const [params] = useSearchParams();
  const [hostView, setHostView] = useState(params.get("view") === "host");
  const state = source.kind === "live" ? source.state : undefined;
  const access: QuestionAccess =
    source.kind === "live" ? source.access : source.kind === "unlinked" ? { kind: "unlinked" } : { kind: "no-conversation" };
  const card = (q: Question) => (
    <QuestionCard key={q.id} question={q} asked={state ? findQuestion(state.events, q.id) : undefined} access={access} where={where} />
  );
  const renderQuestion = (id: string) => {
    const q = questions.find((x) => x.id === id);
    return q ? card(q) : undefined;
  };
  const inline = new Set(state?.events.map(questionIdOf).filter((id): id is string => id !== undefined));
  const trailing = questions.filter((q) => !inline.has(q.id));
  const count = state ? readers(state) : undefined;

  return (
    <WidgetCard
      label={conversation.conversation}
      title={
        <span className="flex flex-wrap items-center gap-2.5">
          {conversation.conversation} <E2EEBadge />
        </span>
      }
      description={count === undefined ? t.onlyYourDevices : t.readableOn(count)}
      action={
        state ? (
          <span className="flex flex-wrap items-center gap-x-4 gap-y-2">
            {!hostView ? <ShowWorkingSwitch /> : null}
            <Switch
              role="switch"
              size="sm"
              label={conversation.showHost}
              labelClassName="text-[13px] text-gray-700"
              checked={hostView}
              onChange={() => setHostView((v) => !v)}
            />
          </span>
        ) : undefined
      }
    >
      {source.kind === "unlinked" ? <Locked /> : null}
      {source.kind === "none" ? (
        <EmptyState compact icon={<ChatsCircle weight="duotone" />} title={t.noneTitle}>
          {t.noneBody}
        </EmptyState>
      ) : null}
      {state && hostView ? <HostRecord state={state} /> : null}
      {state && !hostView ? (
        <EventList state={state} agent={agent} renderQuestion={renderQuestion} />
      ) : null}
      {!hostView ? <div className="mt-4 space-y-4">{trailing.map(card)}</div> : null}
      {questions.length === 0 ? (
        <p className="mb-0 mt-4 text-[12.5px] text-gray-500" data-testid="questions-empty">
          {t.noQuestion}
        </p>
      ) : null}
    </WidgetCard>
  );
}

function Locked() {
  const t = useT().conversation.card;
  return (
    <div className="flex items-start gap-3 rounded-lg bg-secondary-lighter/60 p-4 text-[13.5px] text-gray-700">
      <LockKey weight="duotone" className="mt-0.5 h-5 w-5 shrink-0 text-secondary-dark" />
      <p className="m-0">{t.locked(<Link to="/devices">{t.linkThisBrowser}</Link>)}</p>
    </div>
  );
}
